import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Button from '@/components/atoms/Button';
import CarouselButton from '@/components/atoms/CarouselButton';
import Heading from '@/components/atoms/Heading';
import Media from '@/components/atoms/Media';
import { emphasiseText } from '@/helpers/emphasiseText';
import useStagger from '@/hooks/animations/use-stagger';
import { createUniqueSwiperClasses } from '@/utils/create-unique-swiper-classes';
import updateDataLayer from '@/utils/gtml';
import { isHeadlessBrowser } from '@/utils/isHeadlessBrowser';
import * as React from 'react';
import { useRef, useState } from 'react';
import 'swiper/css';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { twMerge } from 'tailwind-merge';
function duplicateItems(items) {
    /*
     * Duplicating the cards prevents the following issues from occurring:
     *
     * - Swiper won't render the card to the left of the first card
     *   when the centered slides option is active and the slides
     *   per view option is auto
     * - Swiper won't loop properly if there are less than 4 cards
     */
    if (items.length === 1) {
        return items;
    }
    if (items.length === 2) {
        return [...items, ...items, ...items];
    }
    return [...items, ...items];
}
const MediaCardListCarousel = ({ instanceID, items, initialActiveIndex = 0, hideCaptions = false, previousLabel = 'Previous', previousIcon = 'arrow-left', nextLabel = 'Next', nextIcon = 'arrow-right', className, isVisible = true, onExternalVideoPlaying, isInEditor, }) => {
    const videoRefs = React.useRef([]);
    const [activeItem, setActiveItem] = useState(items[0]);
    const swiperClasses = createUniqueSwiperClasses(instanceID);
    const swiperRef = useRef();
    const containerRef = React.useRef(null);
    useStagger(containerRef, {
        childrenStaggerFrom: 'start',
    });
    const disableAnimation = isHeadlessBrowser();
    const normalisedItems = duplicateItems(items).map((item) => ({
        ...item,
        mediaCaption: hideCaptions || item.title?.length || item.text?.length ? undefined : item.mediaCaption,
    }));
    const playVideo = (targetVideoIndex) => {
        videoRefs.current.forEach((video, index) => {
            const media = normalisedItems[index]?.media;
            const isAutoplayVideo = media?.mediaType === 'Video' && media?.autoPlay;
            if (targetVideoIndex === index && isAutoplayVideo) {
                video?.play();
            }
            else {
                video?.pause();
            }
        });
    };
    React.useEffect(() => {
        if (items.length === 1) {
            // If there is only one item, then there is never a need to scroll to a slide.
            return;
        }
        swiperRef.current?.slideReset(0);
        swiperRef.current?.slideToLoop(items.length + initialActiveIndex, 0);
        playVideo(initialActiveIndex);
    }, [initialActiveIndex, swiperRef]);
    return (_jsxs("div", { ref: containerRef, className: twMerge('overflow-y-clip', className), "data-show-after-hydration": true, children: [_jsx(Swiper, { modules: [Navigation, Pagination], pagination: {
                    el: `.${swiperClasses.fraction}`,
                    type: 'fraction',
                    clickable: true,
                    renderFraction: (currentClass, totalClass) => {
                        return `<span class="text-text-links ${currentClass}"></span> / <span class="${totalClass}"></span>`;
                    },
                    formatFractionCurrent: (number) => ((number - 1) % items.length) + 1,
                    formatFractionTotal: () => items.length,
                }, loop: true, watchSlidesProgress: true, className: "relative w-full", spaceBetween: 40, centeredSlides: true, slidesPerView: "auto", breakpoints: {
                    0: {
                        spaceBetween: 12,
                    },
                    768: {
                        spaceBetween: 24,
                    },
                }, onBeforeInit: (swiper) => {
                    swiperRef.current = swiper;
                }, onRealIndexChange: (swiper) => {
                    setActiveItem(normalisedItems[swiper.realIndex]);
                    playVideo(swiper.realIndex);
                }, navigation: {
                    prevEl: `.${swiperClasses.previousButton}, .${swiperClasses.previousButtonMobile}`,
                    nextEl: `.${swiperClasses.nextButton}, .${swiperClasses.nextButtonMobile}`,
                }, wrapperClass: twMerge(!disableAnimation &&
                    '[&_.swiper-slide>*]:duration-500 [&_.swiper-slide.swiper-slide-prev>*]:origin-right [&_.swiper-slide.swiper-slide-next>*]:origin-left [&_.swiper-slide>*]:transition-[opacity,transform] [&_.swiper-slide:not(.swiper-slide-active)>*]:opacity-50 [&_.swiper-slide:not(.swiper-slide-active)>*]:scale-[0.8]'), children: normalisedItems.map((item, index) => {
                    const isActiveSlide = isVisible && index === swiperRef.current?.realIndex;
                    return (_jsx(SwiperSlide, { className: twMerge('aspect-[4/5] !w-[calc(100%-(2*var(--site-padding)))] sm:aspect-square md:aspect-[5/4] md:!w-auto lg:aspect-[6/4] xl:aspect-[7/4] 2xl:aspect-[16/9]', isInEditor ? 'md:!h-[520px] lg:!h-[550px] xl:!h-[630px]' : 'md:!h-[52vh] lg:!h-[55vh] xl:!h-[63vh]'), inert: isActiveSlide ? undefined : '', children: _jsx(Media, { media: item.media, containerClassName: "w-full h-full", mediaClassName: "w-full h-full", imageClassName: "w-full h-full", videoClassName: "w-full h-full object-cover object-center", videoAutoplayMode: "Manual", videoRef: (ref) => (videoRefs.current[index] = ref), caption: item.mediaCaption, imageSizes: {
                                base: { vw: 9 / 10, ratio: 4 / 5 },
                                sm: { vw: 8 / 10, ratio: 1 },
                                md: { ratio: 5 / 4 },
                                lg: { ratio: 6 / 4 },
                                xl: { ratio: 7 / 4 },
                                '2xl': { ratio: 16 / 9 },
                            }, onExternalVideoPlaying: onExternalVideoPlaying }) }, `${item.media.componentID}-${index}`));
                }) }), _jsxs("div", { className: "mx-site-padding flex flex-col gap-2 py-1.5 md:flex-row md:justify-between md:py-2.5", children: [_jsx("div", { children: activeItem !== undefined && (_jsxs("div", { "aria-live": "polite", className: "flex flex-col items-start gap-1", children: [activeItem.title && (_jsx(Heading, { variant: "sm", as: "h2", children: emphasiseText(activeItem.title) })), activeItem.text && (_jsx("div", { dangerouslySetInnerHTML: { __html: activeItem.text }, className: "rich-text text-text-body" })), activeItem.link && (_jsx(Button, { variant: "text-only", width: "auto", children: (props) => (_jsx("a", { ...props, href: activeItem.link?.url, target: activeItem.link?.target, children: activeItem.link?.text })) }))] })) }), _jsx("div", { children: _jsxs("div", { className: "flex items-center justify-between gap-2.5", children: [_jsx("div", { className: twMerge(swiperClasses.fraction, 'inline-flex gap-0.25 text-text-body') }), _jsxs("div", { className: "flex gap-1.5", children: [_jsx(CarouselButton, { variant: "outline", icon: previousIcon, label: previousLabel, className: swiperClasses.previousButton, onClick: () => {
                                                updateDataLayer({
                                                    event: 'carousel',
                                                    component: 'Media Card List',
                                                    carousel_interaction: previousLabel,
                                                });
                                            } }), _jsx(CarouselButton, { variant: "outline", icon: nextIcon, label: nextLabel, className: swiperClasses.nextButton, onClick: () => {
                                                updateDataLayer({
                                                    event: 'carousel',
                                                    component: 'Media Card List',
                                                    carousel_interaction: nextLabel,
                                                });
                                            } })] })] }) })] })] }));
};
export default MediaCardListCarousel;
